import React from 'react';

import clsx from 'clsx';

import type { BaseComponentProps } from '@coursera/cds-common';

import type { ButtonProps } from '@core/Button';
import { CloseButton } from '@core/dialogs/common/CloseButton';
import { useTemporaryDrawerContext } from '@core/drawers/TemporaryDrawer/TemporaryDrawerContextProvider';
import Typography from '@core/Typography2';

import getHeaderCss, { classes } from './styles/getHeadingGroupCss';

export type Props = {
  children: React.ReactNode;

  /**
   * The text to display as drawer heading support text
   */
  supportText?: string;

  /**
   * Additional action that will be placed in the heading.
   * Can be used for a Back button.
   */
  backAction?: React.ReactElement<ButtonProps>;
} & BaseComponentProps<'div'>;

const HeadingGroup = React.forwardRef(function HeadingGroup(
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const { children, supportText, backAction, className, id, ...rest } = props;

  const { onClose, headingGroupId } = useTemporaryDrawerContext();

  return (
    <div
      ref={ref}
      className={clsx(className, {
        [classes.hasBackAction]: backAction !== undefined,
      })}
      css={getHeaderCss}
      {...rest}
    >
      <div className={classes.header} id={id ?? headingGroupId}>
        <Typography
          className={classes.heading}
          component="h2"
          variant="titleSmall"
        >
          {children}
        </Typography>

        {supportText && (
          <Typography className={classes.subheading} component="p">
            {supportText}
          </Typography>
        )}
      </div>
      <div className={classes.toolbar}>
        <div className={classes.toolbarStartCol}>{backAction}</div>
        <div className={classes.toolbarEndCol}>
          <CloseButton onClick={onClose} />
        </div>
      </div>
    </div>
  );
});

if (__DEV__) {
  HeadingGroup.displayName = 'TemporaryDrawer.HeadingGroup';
}

export default HeadingGroup;
